import { Alert, Divider, Group, Stack, Table, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { avgCostByProduct, useStockOnHand } from '../api';
import { estimateValueCents, formatQty, formatRands } from '../format';
import { ProductName } from './EntityNames';

export interface MarginPreviewLine {
  key: string;
  productId: string;
  productLabel: string;
  qty: string;
  /** Wire cents-per-BASE-UOM-unit string (already converted from the form's Rand
   * input via `randToCents`). */
  priceCentsPerUnit: string;
}

interface DispatchMarginPreviewProps {
  siteId: string | undefined;
  lines: MarginPreviewLine[];
}

/** "Review with a ledger-effect + margin preview" (BUILD_SPEC.md §8.4 step 3,
 * §11: "every posting screen shows a plain-language preview of ledger effects
 * before Post"). Sale value is known exactly (qty x the entered price); the COST
 * side is an ESTIMATE computed client-side from the product×site pool's current
 * WAC average (`GET /reporting/stock-on-hand`) — the authoritative cost (and
 * therefore margin) is only known once `recordMovement` actually runs at Post
 * (CLAUDE.md: avg cost is always derived, never stored, and can move between
 * preview and Post). Mirrors features/stores-transfers/components/
 * LedgerPreview.tsx's own framing. */
export function DispatchMarginPreview({ siteId, lines }: DispatchMarginPreviewProps) {
  const { data, isLoading } = useStockOnHand(siteId);
  const avgCosts = avgCostByProduct(data?.items);

  const validLines = lines.filter((line) => line.productId && line.qty && Number(line.priceCentsPerUnit) >= 0);
  const rows = validLines.map((line) => {
    const avg = avgCosts.get(line.productId);
    const qty = Number(line.qty);
    const priceCents = Number(line.priceCentsPerUnit);
    const saleValueCents = Number.isFinite(qty) && Number.isFinite(priceCents) ? qty * priceCents : 0;
    const estCostCents = estimateValueCents(avg, line.qty);
    const estMarginCents = estCostCents === null ? null : saleValueCents - estCostCents;
    return { ...line, saleValueCents, estCostCents, estMarginCents };
  });

  const totalSaleCents = rows.reduce((sum, r) => sum + r.saleValueCents, 0);
  const anyUnknown = rows.some((r) => r.estCostCents === null);
  const totalMarginCents = rows.reduce((sum, r) => sum + (r.estMarginCents ?? 0), 0);

  return (
    <Stack gap="sm">
      <Alert icon={<IconInfoCircle size={18} />} color="graphite" variant="light">
        Sale value is exact (qty × price). Cost and margin are estimated at each product&apos;s current weighted-average
        cost — the exact figure is confirmed when you Post.
      </Alert>
      <Table.ScrollContainer minWidth={640}>
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Product</Table.Th>
              <Table.Th>Qty</Table.Th>
              <Table.Th ta="right">Sale value</Table.Th>
              <Table.Th ta="right">Est. cost</Table.Th>
              <Table.Th ta="right">Est. margin</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.key}>
                <Table.Td>{row.productLabel || <ProductName id={row.productId} />}</Table.Td>
                <Table.Td>{formatQty(row.qty)}</Table.Td>
                <Table.Td ta="right">{formatRands(row.saleValueCents)}</Table.Td>
                <Table.Td ta="right">{isLoading ? '…' : row.estCostCents !== null ? formatRands(row.estCostCents) : 'No prior cost'}</Table.Td>
                <Table.Td ta="right">{isLoading ? '…' : row.estMarginCents !== null ? formatRands(row.estMarginCents) : '—'}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Divider />
      <Group justify="space-between">
        <Text fw={600}>Total sale value: {formatRands(totalSaleCents)}</Text>
        <Text fw={700} size="lg">
          Estimated total margin: {formatRands(totalMarginCents)}
        </Text>
      </Group>
      {anyUnknown && (
        <Text size="sm" c="dimmed">
          Some lines have no prior stock at this site, so their cost/margin can&apos;t be estimated yet — they will still
          Post correctly.
        </Text>
      )}
    </Stack>
  );
}
